if (typeof TimelineSlider !== 'function') {

  class TimelineSlider extends HTMLElement {

    #swiper = null;
    #resizeObserver = null;
    #labelClickHandlers = [];
    #usesWindowResize = false;
    #scrollEl = null;
    #onScroll = null;
    #selectedIndex = 0;

    connectedCallback() {
      if (this.#swiper || this.dataset.initialized === 'true') {
        return;
      }

      if (typeof Swiper === 'undefined') {
        return;
      }

      const swiperEl = this.querySelector('.timeline-slider__swiper');
      const slideCount = this.querySelectorAll('.timeline-slider__slide').length;

      if (!swiperEl || slideCount === 0) {
        return;
      }

      this.dataset.initialized = 'true';

      const prevButton = this.querySelector('.timeline-slider__button--prev');
      const nextButton = this.querySelector('.timeline-slider__button--next');
      const spaceBetween = parseInt(this.dataset.spaceBetween, 10) || 24;

      if (prevButton) {
        prevButton.setAttribute('aria-label', this.dataset.prevLabel || 'Previous slide');
      }

      if (nextButton) {
        nextButton.setAttribute('aria-label', this.dataset.nextLabel || 'Next slide');
      }

      if (slideCount > 1) {
        this.#swiper = new Swiper(swiperEl, {
          slidesPerView: 'auto',
          spaceBetween,
          slidesOffsetAfter: 0,
          navigation: {
            prevEl: prevButton,
            nextEl: nextButton,
          },
          on: {
            init: (swiper) => {
              this.#selectedIndex = swiper.activeIndex;
              this.#updateTimeline(this.#selectedIndex);
              requestAnimationFrame(() => {
                this.#updateSlidesOffsetAfter(swiper);
              });
            },
            resize: (swiper) => {
              this.#updateSlidesOffsetAfter(swiper);
            },
            slideChange: (swiper) => {
              this.#selectedIndex = swiper.activeIndex;
              this.#updateTimeline(this.#selectedIndex);
            },
          },
        });
      } else {
        this.#selectedIndex = 0;
        this.#updateTimeline(0);
        if (prevButton) prevButton.disabled = true;
        if (nextButton) nextButton.disabled = true;
      }

      this.#bindLabelClicks();
      this.#bindResize();
      this.#bindScroll();
      this.#bindFonts();
    }

    disconnectedCallback() {
      this.#destroy();
    }

    #destroy() {
      this.#labelClickHandlers.forEach(({ button, handler }) => {
        button.removeEventListener('click', handler);
      });
      this.#labelClickHandlers = [];

      if (this.#resizeObserver) {
        this.#resizeObserver.disconnect();
        this.#resizeObserver = null;
      }

      if (this.#usesWindowResize) {
        window.removeEventListener('resize', this.#handleResize);
        this.#usesWindowResize = false;
      }

      if (this.#scrollEl && this.#onScroll) {
        this.#scrollEl.removeEventListener('scroll', this.#onScroll);
        this.#scrollEl = null;
        this.#onScroll = null;
      }

      if (this.#swiper) {
        this.#swiper.destroy(true, true);
        this.#swiper = null;
      }

      delete this.dataset.initialized;
    }

    #bindLabelClicks() {
      this.querySelectorAll('.timeline-slider__label').forEach((button, index) => {
        const handler = () => {
          this.#selectedIndex = index;
          this.#updateTimeline(index);

          if (this.#swiper) {
            this.#swiper.slideTo(index);
          }
        };

        button.addEventListener('click', handler);
        this.#labelClickHandlers.push({ button, handler });
      });
    }

    #bindResize() {
      const timeline = this.querySelector('.timeline-slider__timeline');

      if (!timeline || typeof ResizeObserver === 'undefined') {
        window.addEventListener('resize', this.#handleResize);
        this.#usesWindowResize = true;
        return;
      }

      this.#resizeObserver = new ResizeObserver(() => {
        this.#updateTimeline(this.#selectedIndex);
      });
      this.#resizeObserver.observe(timeline);
    }

    #bindScroll() {
      this.#scrollEl = this.querySelector('.timeline-slider__scroll');

      if (!this.#scrollEl) {
        return;
      }

      this.#onScroll = () => {
        this.#updateTimeline(this.#selectedIndex);
      };

      this.#scrollEl.addEventListener('scroll', this.#onScroll, { passive: true });
    }

    #bindFonts() {
      if (!document.fonts || !document.fonts.ready) {
        return;
      }

      document.fonts.ready.then(() => {
        if (this.isConnected) {
          this.#updateTimeline(this.#selectedIndex);
        }
      });
    }

    #handleResize = () => {
      this.#updateTimeline(this.#selectedIndex);
    };

    #updateSlidesOffsetAfter(swiper) {
      const lastSlide = swiper.slides[swiper.slides.length - 1];

      if (!lastSlide) {
        return;
      }

      const offset = Math.max(0, swiper.width - lastSlide.offsetWidth);

      if (Math.abs((swiper.params.slidesOffsetAfter || 0) - offset) < 1) {
        return;
      }

      swiper.params.slidesOffsetAfter = offset;
      swiper.update();
    }

    #updateTimeline(index) {
      const labels = this.querySelectorAll('.timeline-slider__label');
      const progress = this.querySelector('.timeline-slider__progress');
      const track = this.querySelector('.timeline-slider__track');

      labels.forEach((label, i) => {
        const isActive = i === index;
        label.classList.toggle('is-active', isActive);
        label.setAttribute('aria-current', isActive ? 'true' : 'false');
        label.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      const activeLabel = labels[index];

      if (!activeLabel || !progress || !track) {
        return;
      }

      const trackRect = track.getBoundingClientRect();
      const labelRect = activeLabel.getBoundingClientRect();
      const scaleX = trackRect.width === 0 ? 1 : track.offsetWidth / trackRect.width;
      const left = (labelRect.left - trackRect.left) * scaleX;
      const width = labelRect.width * scaleX;

      progress.style.width = `${Math.max(width, 0)}px`;
      progress.style.transform = `translateX(${left}px)`;

      if (this.#scrollEl && window.matchMedia('(max-width: 749px)').matches) {
        const scrollRect = this.#scrollEl.getBoundingClientRect();
        const scrollLeft =
          this.#scrollEl.scrollLeft +
          (labelRect.left - scrollRect.left) -
          (this.#scrollEl.clientWidth / 2) +
          (labelRect.width / 2);

        this.#scrollEl.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: 'smooth',
        });
      }
    }

  }

  if (typeof customElements.get('timeline-slider') === 'undefined') {
    customElements.define('timeline-slider', TimelineSlider);
  }

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('timeline-slider').forEach((slider) => {
      if (slider.isConnected) {
        slider.connectedCallback();
      }
    });
  });

  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('timeline-slider').forEach((slider) => {
      slider.disconnectedCallback();
    });
  });

}
